import { App as CapApp } from '@capacitor/app';
import { AnimatePresence, motion } from 'framer-motion';
import { ChevronLeft } from 'lucide-react';
import { useEffect, useRef, type ReactNode } from 'react';
import { cn } from '../lib/cn';
import { isNative } from '../lib/platform';

type Accent = 'expense' | 'income' | 'neutral';

type Props = {
  open: boolean;
  onClose: () => void;
  title: string;
  /** Reserved for future tone tweaks; currently always renders neutral. */
  titleAccent?: Accent;
  children: ReactNode;
  /** Optional trailing slot for actions like a Save shortcut or menu. */
  rightAction?: ReactNode;
};

/**
 * iOS-style full-screen modal that pushes in from the right and exits
 * the same way. Renders fixed inset-0, no backdrop. Header has a back
 * chevron, centered title, and an optional right slot.
 *
 * Hardware back integration:
 *   - Native (Capacitor App): listens for backButton and calls onClose
 *     when this modal is the topmost open instance.
 *   - Web: pushes a history entry on open and listens for popstate so
 *     the browser back button closes the modal first.
 *
 * Body scroll on the underlying page is locked while open.
 */
export function FullScreenModal({
  open,
  onClose,
  title,
  children,
  rightAction,
}: Props) {
  // Track whether THIS instance owns the topmost native back / browser
  // history entry. Only the topmost open modal should consume back.
  const topmost = useRef(false);

  // Hold the latest onClose in a ref so effects below can read it
  // without taking it as a dependency. If onClose were a dep, every
  // render would re-run the popstate effect — its cleanup pops a
  // history entry, but the next effect synchronously installs a fresh
  // listener, and the deferred popstate then calls onClose on that
  // new listener. That manifested as the discard dialog firing on
  // every numpad tap.
  const onCloseRef = useRef(onClose);
  useEffect(() => {
    onCloseRef.current = onClose;
  }, [onClose]);

  // Body scroll lock + Escape key.
  useEffect(() => {
    if (!open) return;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onCloseRef.current();
    };
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = prevOverflow;
      window.removeEventListener('keydown', onKey);
    };
  }, [open]);

  // Native hardware back button.
  useEffect(() => {
    if (!open || !isNative()) return;
    topmost.current = true;
    let handle: { remove: () => void } | undefined;
    void CapApp.addListener('backButton', () => {
      if (topmost.current) onCloseRef.current();
    }).then((h) => {
      handle = h;
    });
    return () => {
      topmost.current = false;
      handle?.remove();
    };
  }, [open]);

  // Web back button — push a sentinel history entry on open, close on
  // popstate. We only react to the modal-marked entry we ourselves
  // pushed.
  useEffect(() => {
    if (!open || isNative()) return;
    if (typeof window === 'undefined') return;
    const marker = { __marginModal: true };
    window.history.pushState(marker, '');
    const onPop = () => {
      onCloseRef.current();
    };
    window.addEventListener('popstate', onPop);
    return () => {
      window.removeEventListener('popstate', onPop);
      if (
        window.history.state &&
        (window.history.state as { __marginModal?: boolean }).__marginModal
      ) {
        window.history.back();
      }
    };
  }, [open]);

  return (
    <AnimatePresence>
      {open ? (
        <motion.div
          key="fsmodal"
          role="dialog"
          aria-modal="true"
          aria-label={title}
          initial={{ x: '100%' }}
          animate={{ x: 0 }}
          exit={{ x: '100%' }}
          transition={{ type: 'spring', stiffness: 280, damping: 30 }}
          className={cn(
            'fixed inset-0 z-50 flex flex-col bg-bg',
            'pt-[env(safe-area-inset-top)]',
          )}
        >
          {/* Header */}
          <div className="h-14 shrink-0 px-2 flex items-center justify-between border-b border-surface-secondary">
            <button
              type="button"
              onClick={onClose}
              aria-label="Back"
              className="w-11 h-11 inline-flex items-center justify-center text-text-primary"
            >
              <ChevronLeft size={26} strokeWidth={1.75} />
            </button>
            <h2 className="flex-1 text-center text-[17px] font-semibold text-text-primary truncate px-2">
              {title}
            </h2>
            <div className="w-11 h-11 inline-flex items-center justify-center">
              {rightAction}
            </div>
          </div>

          {/* Body */}
          <div
            className="flex-1 overflow-y-auto overscroll-contain pb-[env(safe-area-inset-bottom)]"
            style={{
              // Lock to vertical scrolling so any horizontal-scroll
              // children (filter pill rows, etc.) don't fight the
              // outer scroll for ownership of a touch — a known cause
              // of the visible jitter on iOS.
              touchAction: 'pan-y',
              // Force a dedicated compositor layer so the slide-in
              // transform applied by Framer doesn't get re-rasterized
              // per scroll frame at sub-pixel offsets.
              transform: 'translate3d(0,0,0)',
              WebkitOverflowScrolling: 'touch',
            }}
          >
            {children}
          </div>
        </motion.div>
      ) : null}
    </AnimatePresence>
  );
}
